import { useRef, type ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { BiHistory, BiPlusCircle, BiSolidEditAlt } from 'react-icons/bi';
import { useWidgetRetryFocus, type WidgetFetchStatus } from '@/lib/useWidgetRetryFocus';
import { ACTIVITY_ENTITY_LABEL_KEY } from '@/lib/dashboardWidgets';
import { formatDateTime, formatRelativeTime } from '@/lib/format';
import type { DashboardActivityItem } from '@/types/shared';
import './ActivityFeedWidget.css';

interface ActivityFeedWidgetProps {
  items: DashboardActivityItem[];
  status: WidgetFetchStatus;
  onRetry: () => void;
}

const SKELETON_ROW_COUNT = 6;

/** Sentinel tokens swapped into the entity-label i18n template so `renderEntityLabel` can split
 * the (already-translated) string back apart and wrap only the name/code substrings in `<bdi>` —
 * see `dashboardWidgets.ts` for why the template still only knows one `{{name}}` field. Picked to
 * be astronomically unlikely to collide with real entity-label/student-code text. */
const NAME_TOKEN = '@@FA_DASHBOARD_NAME@@';
const CODE_TOKEN = '@@FA_DASHBOARD_CODE@@';

/**
 * The recent-activity feed widget (`11-dashboard-widget-grid-ux.md §3`). Fixed last-10 list, own
 * independent fetch/skeleton/error/retry from the 5 stat cards. Loading = 6 skeleton rows.
 * Loaded+0 items = a true reassuring empty state (never styled as an error). Loaded+items = each
 * row is its own link (create vs. edit distinguished by glyph *and* text label, never color
 * alone).
 */
export function ActivityFeedWidget({ items, status, onRetry }: ActivityFeedWidgetProps) {
  const { t, i18n } = useTranslation();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const retryButtonRef = useRef<HTMLButtonElement>(null);
  const retriedRef = useRef(false);
  const headingId = 'dashboard-activity-feed-heading';

  useWidgetRetryFocus(status, retriedRef, headingRef, retryButtonRef);

  function handleRetryClick() {
    retriedRef.current = true;
    onRetry();
  }

  const title = t('dashboard.widgets.activityFeed.title');
  const retryAriaLabel = t('dashboard.widgets.common.retryAriaLabelTemplate', {
    widgetTitle: title,
  });
  const isEmpty = status === 'loaded' && items.length === 0;
  const isLoadedWithItems = status === 'loaded' && items.length > 0;

  return (
    <section
      className="fa-activity-feed"
      aria-labelledby={headingId}
      aria-busy={status === 'loading'}
    >
      <h3 id={headingId} ref={headingRef} tabIndex={-1} className="fa-activity-feed__title">
        <BiHistory className="fa-activity-feed__title-icon" aria-hidden="true" />
        {title}
      </h3>

      {status === 'loading' && (
        // The heading above is real, visible text (§5 "hear the heading only" while busy); this
        // wrapper's `aria-label` is a static (non-live) name for the skeleton sub-region — only
        // read if a screen-reader user explores into it, never auto-announced.
        <div
          className="fa-activity-feed__skeleton-wrap"
          aria-label={t('dashboard.widgets.common.loadingAriaLabel')}
        >
          <ul className="fa-activity-feed__list">
            {Array.from({ length: SKELETON_ROW_COUNT }).map((_, idx) => (
              <li key={idx} className="fa-activity-feed__row-skeleton" aria-hidden="true">
                <span className="fa-activity-feed__skeleton-line fa-activity-feed__skeleton-line--primary" />
                <span className="fa-activity-feed__skeleton-line fa-activity-feed__skeleton-line--secondary" />
              </li>
            ))}
          </ul>
        </div>
      )}

      {status === 'error' && (
        <div className="fa-activity-feed__error" role="status">
          <p>{t('dashboard.widgets.common.errorMessage')}</p>
          <button
            type="button"
            ref={retryButtonRef}
            className="fa-activity-feed__retry-btn"
            onClick={handleRetryClick}
            aria-label={retryAriaLabel}
          >
            {t('dashboard.widgets.common.retryLabel')}
          </button>
        </div>
      )}

      {isEmpty && (
        <div className="fa-activity-feed__empty">
          <span className="fa-activity-feed__empty-icon" aria-hidden="true">
            <BiHistory />
          </span>
          <p className="fa-activity-feed__empty-title">
            {t('dashboard.widgets.activityFeed.emptyTitle')}
          </p>
          <p className="fa-activity-feed__empty-body">
            {t('dashboard.widgets.activityFeed.emptyBody')}
          </p>
        </div>
      )}

      {isLoadedWithItems && (
        <ul className="fa-activity-feed__list">
          {items.map((item) => (
            <ActivityFeedRow
              key={`${item.entityType}-${item.entityId}`}
              item={item}
              language={i18n.language}
              t={t}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

interface ActivityFeedRowProps {
  item: DashboardActivityItem;
  language: string;
  t: TFunction;
}

function ActivityFeedRow({ item, language, t }: ActivityFeedRowProps) {
  const isCreated = item.action === 'created';
  const actionLabelKey = isCreated
    ? 'dashboard.widgets.activityFeed.actionCreated'
    : 'dashboard.widgets.activityFeed.actionEdited';
  const actorName = item.actor?.fullName ?? t('dashboard.widgets.activityFeed.systemActor');
  const relativeTime = formatRelativeTime(item.at, language);
  const exactTime = formatDateTime(item.at, language);

  return (
    <li className="fa-activity-feed__row">
      <Link to={item.deepLink} className="fa-activity-feed__link">
        <span className="fa-activity-feed__line1">
          <span
            className={`fa-activity-feed__glyph-chip fa-activity-feed__glyph-chip--${
              isCreated ? 'success' : 'info'
            }`}
            aria-hidden="true"
          >
            {isCreated ? <BiPlusCircle /> : <BiSolidEditAlt />}
          </span>
          <span className="fa-activity-feed__action">{t(actionLabelKey)}</span>
          {' — '}
          {renderEntityLabel(t, item)}
        </span>
        <span className="fa-activity-feed__line2">
          <bdi>{actorName}</bdi>
          {' · '}
          <bdi title={exactTime}>{relativeTime}</bdi>
        </span>
      </Link>
    </li>
  );
}

/** Splits the translated entity-label template back around its `{{name}}`/`{{studentCode}}`
 * substrings (via sentinel tokens) so each can be individually wrapped in `<bdi>` — student
 * codes additionally get `.fa-ltr-isolate` so they never reverse inside the Arabic sentence
 * (`10-design-system.md §2.3`). */
function renderEntityLabel(t: TFunction, item: DashboardActivityItem): ReactNode {
  const templateKey = ACTIVITY_ENTITY_LABEL_KEY[item.entityType];
  const raw = t(templateKey, {
    name: NAME_TOKEN,
    studentCode: CODE_TOKEN,
    interpolation: { escapeValue: false },
  });
  const segments = raw.split(new RegExp(`(${NAME_TOKEN}|${CODE_TOKEN})`, 'g'));

  return segments
    .filter((segment) => segment.length > 0)
    .map((segment, idx) => {
      if (segment === NAME_TOKEN) {
        return <bdi key={`name-${idx}`}>{item.entityLabel}</bdi>;
      }
      if (segment === CODE_TOKEN) {
        return item.studentCode ? (
          <bdi key={`code-${idx}`} className="fa-ltr-isolate">
            {item.studentCode}
          </bdi>
        ) : null;
      }
      return <span key={`text-${idx}`}>{segment}</span>;
    });
}
